"use client";

import {
  TypeCheckoutConfiguration,
  TypePackageAddOnItemSkeleton,
  TypePackageDetailItem,
  TypePackageModificationItem,
} from "@contentful/types";
import { PaymentIntentResponse } from "@yardzen/rest-api-client";
import { Button } from "@yardzen/ui/components/button";
import { Card } from "@yardzen/ui/components/card";
import { H5 } from "@yardzen/ui/components/h5";
import { Input } from "@yardzen/ui/components/input";
import { LineDivider } from "@yardzen/ui/components/line-divider";
import { cn } from "@yardzen/ui/lib/utils";
import { Entry } from "contentful";
import Image from "next/image";
import { PackageDisplayAddOns } from "./PackageDisplayAddOns";

export function PackageDetailItemCheckout({
  packageDetailItem,
  checkoutConfiguration,
  currentAddons,
  packageModificationItem,
  paymentIntent,
  chooseAddonItem,
  promoCodesEnabled,
}: {
  packageDetailItem: TypePackageDetailItem<"WITHOUT_UNRESOLVABLE_LINKS">;
  checkoutConfiguration: TypeCheckoutConfiguration<"WITHOUT_UNRESOLVABLE_LINKS">;
  currentAddons: string[];
  packageModificationItem?: TypePackageModificationItem<"WITHOUT_UNRESOLVABLE_LINKS">;
  paymentIntent: PaymentIntentResponse;
  chooseAddonItem: (
    addonItem: Entry<
      TypePackageAddOnItemSkeleton,
      "WITHOUT_UNRESOLVABLE_LINKS",
      string
    >,
    checked: boolean,
  ) => void;
  promoCodesEnabled: boolean;
}) {
  const { checkoutImage } = packageDetailItem?.fields || {};
  const fullPackageName = packageModificationItem
    ? packageModificationItem?.fields?.name
    : packageDetailItem?.fields.fullPackageName;
  const { product, total, subTotal, appliedAddons, discount } = paymentIntent;

  const productPrice = product.price / 100;
  const totalPrice = total / 100;
  const subTotalPrice = subTotal / 100;
  const discountPrice = discount?.discount ? discount.discount / 100 : 0;
  const contentfulAddOnInfo = packageDetailItem?.fields.addOnItems;

  return (
    <div className="w-full md:max-w-[560px]">
      <Card
        variant="default"
        className="mb-4 flex flex-col gap-3 p-4 drop-shadow-md"
      >
        <H5 className="font-display mb-4 text-left text-2xl font-normal">
          Order Summary
        </H5>
        <div className="flex flex-row justify-between gap-2">
          <Image
            src={`https:${checkoutImage?.fields.image?.fields.file?.url}`}
            alt={checkoutImage?.fields.description ?? ""}
            height={74}
            width={95}
            className="h-[74px] w-[95px] rounded-lg object-cover"
          />
          <LineItem name={fullPackageName ?? ""} price={productPrice} />
        </div>

        {promoCodesEnabled && (
          <div className="flex gap-2 py-2">
            <Input
              placeholder="Discount Code"
              className="border-system-grayscale-20 w-full rounded-md"
            />
            <Button
              variant="outline"
              className="border-system-grayscale-20 font-body w-[78px] font-semibold"
            >
              Apply
            </Button>
          </div>
        )}
        {contentfulAddOnInfo && paymentIntent && (
          <PackageDisplayAddOns
            currentAddOns={currentAddons}
            paymentIntent={paymentIntent}
            contentfulAddons={contentfulAddOnInfo}
            onToggleAddon={(stripeProductId, checked) => {
              const addonEntry = contentfulAddOnInfo.find(
                (addon) => addon?.fields.stripeProductId === stripeProductId,
              );
              if (addonEntry) {
                chooseAddonItem(addonEntry, checked);
              }
            }}
            selectedAddonNames={
              (appliedAddons && appliedAddons.map((a) => a.id)) || []
            }
          />
        )}
        {appliedAddons && (
          <>
            {appliedAddons.map((addon, i) => (
              <LineItem
                key={`pdic-${addon.name}-${i}`}
                name={`+ ${addon.name}`}
                price={addon.price / 100}
              />
            ))}
            <LineDivider
              className="h-2 border-black opacity-20"
              orientation="horizontal"
              variant="dotted"
            />
          </>
        )}
        <LineItem name={"Subtotal"} price={subTotalPrice} />
        {discount && discountPrice !== 0 && (
          <LineItem name={"Discount"} price={-discountPrice} />
        )}
        <LineDivider
          className="h-2 border-black opacity-20"
          orientation="horizontal"
          variant="dotted"
        />

        <LineItem
          name={"Total"}
          price={totalPrice}
          className="text-2xl"
          classNamePrice="text-2xl font-normal"
        />
      </Card>
    </div>
  );

  function LineItem({
    name,
    price,
    className,
    classNamePrice,
  }: {
    name: string;
    price: number;
    className?: string;
    classNamePrice?: string;
  }) {
    return (
      <div className="space-between flex w-full flex-row justify-between gap-2">
        <div
          className={cn(
            "font-display text-brand-secondary-dark text-lg",
            className,
          )}
        >
          {name}
        </div>
        <div
          className={cn(
            "font-body text-brand-secondary-dark text-lg font-semibold",
            classNamePrice,
          )}
        >
          ${Number(price)}
        </div>
      </div>
    );
  }
}
